import useDocumentTitle from "@/app/hooks/documentTitleHook"
import { useAppSelector } from "@/app/hooks/reduxHooks"
import { buildSearchUrl } from "@/app/utils/searchUtils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent } from "@/components/ui/card"
import ContactInformation from "@/components/ui/contactinformation"
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog"
import ExportLeads from "@/components/ui/modals/exportleads"
import EditColumn from "@/components/ui/modals/editcolumn"
import type { ColumnOption } from "@/features/searchTable/slice/columnsSlice"
import { openColumnsModal } from "@/features/searchTable/slice/columnsSlice"
import { useAppDispatch } from "@/app/hooks/reduxHooks"
import { DataTableColumn } from "@/interface/searchTable/components"
import { CompanyAttributes, ContactAttributes, SearchApiResponse } from "@/interface/searchTable/search"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useCallback, useEffect, useMemo, useState } from "react"
import DownloadIcon from "../../static/media/icons/download-icon.svg?react"
import { selectSelectedItems } from "../filters/slice/filterSlice"
import { DataTable } from "./baseDataTable"
import { useSearchContactsQuery } from "./slice/apiSlice"
import { openContactInfoForCompany } from "./slice/contactInfoSlice"

export function CompaniesTable() {
  useDocumentTitle("Search Companies")
  const dispatch = useAppDispatch()
  const columnsConfigFromStore = useAppSelector((s) => s.columns.configs.company)

  const selectedFilters = useAppSelector(selectSelectedItems)
  const [pagination, setPagination] = useState({
    page: 1,
    count: 20,
    total: 0,
    lastPage: 1
  })
  const [queryValue, setQueryValue] = useState("")
  const [sortSelected, setSortSelected] = useState<string[]>([])

  // NEW: State for checkbox selection
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [infoCompanyContact, setInfoCompanyContact] = useState<ContactAttributes | null>(null)
  const [infoCompanyDetails, setInfoCompanyDetails] = useState<CompanyAttributes | null>(null)

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage
    }))
  }

  // NEW: Handle individual company selection
  const handleCompanySelect = useCallback((companyId: string) => {
    setSelectedCompanies((prev) => (prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]))
  }, [])

  // NEW: Handle select all companies
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allCompanyIds = data?.data?.map((company) => company.id || "") || []
      setSelectedCompanies(allCompanyIds.filter((id) => id !== ""))
    } else {
      setSelectedCompanies([])
    }
  }

  const transformedFilters = useMemo(() => {
    const filterEntries = Object.entries(selectedFilters)
      .filter(([, values]) => values && values.length > 0)
      .map(([filterType, filterValues]) => ({
        type: filterType,
        values: filterValues.map((value) => ({
          id: value.id,
          text: value.name,
          selectionType: value.type === "include" ? "INCLUDED" : "EXCLUDED"
        }))
      }))
    return filterEntries
  }, [selectedFilters])

  const queryParams = useMemo(
    () => ({
      ...(queryValue && { searchTerm: queryValue }),
      ...(transformedFilters.length > 0 && { filters: transformedFilters })
    }),
    [queryValue, transformedFilters]
  )

  const searchParams = useMemo(
    () => ({
      page: pagination.page,
      count: pagination.count,
      ...(sortSelected.length && { sort: sortSelected.join(",") })
    }),
    [pagination.page, pagination.count, sortSelected]
  )

  const searchUrl = useMemo(() => buildSearchUrl(searchParams, queryParams), [searchParams, queryParams])

  const { data, isLoading, isFetching } = useSearchContactsQuery({ type: "company", buildParams: searchUrl }, { refetchOnMountOrArgChange: true })

  const sortableFields = ["company", "website", "company_linkedin_url"]

  const baseColumns: DataTableColumn<CompanyAttributes>[] = [
    {
      title: "Company Name",
      field: "company",
      width: "w-1/4",
      render: (value) => (
        <div className="flex items-center gap-2">
          {/* <Building2 className="mt-1 size-4 shrink-0" /> */}
          <span className="break-words">{value}</span>
        </div>
      )
    },
    // {
    //   title: "Website",
    //   field: "website",
    //   width: "w-1/5",
    //   render: (value, row) => (
    //     <TooltipProvider>
    //       <div className="flex items-center gap-3">
    //         <Globe className="mt-1 size-4 shrink-0" />
    //         <span className="text-nowrap">{value}</span>
    //         <Tooltip>
    //           <TooltipTrigger asChild>
    //             <a href={row.company_linkedin_url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center">
    //               <LinkedinIcon className="size-4" />
    //             </a>
    //           </TooltipTrigger>
    //           <TooltipContent>
    //             <p>{row.company_linkedin_url?.replace("http://www.", "")}</p>
    //           </TooltipContent>
    //         </Tooltip>
    //       </div>
    //     </TooltipProvider>
    //   )
    // },
    {
      title: "Industry",
      field: "industry",
      width: "w-1/5",
      render: (value) => (
        <div className="flex items-center gap-2">
          {/* <Factory className="mt-1 size-4 shrink-0" /> */}
          {value}
        </div>
      )
    },
    {
      title: "HQ Location",
      field: "location",
      render: (value) => (
        <div className="flex items-center gap-2">
          {/* <MapPin className="mt-1 size-4 shrink-0" /> */}
          {value?.country || <span className="text-sm text-muted-foreground">Not Available</span>}
        </div>
      )
    },
    {
      title: "Company Size",
      field: "company_size",
      width: "w-1/6",

      render: () => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-950">240 - 500</span>
        </div>
      )
    },
    {
      title: "Actions",
      field: "actions" as keyof CompanyAttributes,
      width: "w-[140px]",
      // render: (_: unknown, row: CompanyAttributes) => (
      //   <Button
      //     size="sm"
      //     variant="outline"
      //     className="h-8 gap-2 border-[#335CFF] px-[10px] py-1 text-xs text-[#335CFF] hover:text-[#335CFF]"
      //     onClick={() =>
      //       setInfoCompanyContact({
      //         _id: "",
      //         company: row.company,
      //         linkedin_url: row.company_linkedin_url ?? undefined,
      //         website: row.website
      //       })
      //     }
      //   >
      //     View Company
      //   </Button>
      // )
      render: (_: unknown, row: CompanyAttributes) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-2 border-[#335CFF] px-[10px] py-1 text-xs text-[#335CFF] hover:text-[#335CFF]"
          onClick={() => {
            setInfoCompanyContact({
              _id: "",
              company: row.company,
              linkedin_url: row.company_linkedin_url ?? undefined,
              website: row.website
            })
            setInfoCompanyDetails(row)
          }}
        >
          View Company
        </Button>
      )
    }
  ]

  const companyColumnsConfig: ColumnOption[] = columnsConfigFromStore

  const visibleOrderedCompanyColumns: DataTableColumn<CompanyAttributes>[] = companyColumnsConfig
    .filter((c) => c.visible)
    .map((cfg) => baseColumns.find((bc) => (bc.field as string) === cfg.field))
    .filter((c): c is DataTableColumn<CompanyAttributes> => Boolean(c))

  useEffect(() => {
    if (data?.meta) {
      setPagination((prev) => ({
        ...prev,
        total: data.meta.total || 0,
        lastPage: data.meta.last_page || 1
      }))
    }
  }, [data?.meta])

  // NEW: Clear selection when data changes (page change, search, etc.)
  useEffect(() => {
    setSelectedCompanies([])
  }, [pagination.page, queryValue, transformedFilters])

  return (
    // <Card className="h-full border-gray-200 bg-white backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950">
    <CardContent className="h-full ">
      <div className="flex h-full flex-col">
        {/* {transformedFilters.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {transformedFilters.map((filter) =>
              filter.values.map((value) => (
                <Badge key={value.id} variant={value.selectionType === "INCLUDED" ? "default" : "destructive"} className="text-xs">
                  {value.text}
                </Badge>
              ))
            )}
          </div>
        )} */}

        {/* NEW: Show selected count */}
        {selectedCompanies.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs">{selectedCompanies.length} selected</span>
            <Button
              variant="default"
              className="h-[28px] bg-[#476CFF19] px-1.5 py-1 text-sm font-medium text-[#335CFF] hover:bg-[#476CFF19] hover:text-[#335CFF]"
              onClick={() => setIsExportOpen(true)}
            >
              <DownloadIcon className="size-5 text-[#335CFF]" />
              Export as CSV
            </Button>
          </div>
        )}

        <div className="flex-1">
          <DataTable<CompanyAttributes>
            columns={visibleOrderedCompanyColumns}
            data={(data as SearchApiResponse<CompanyAttributes>)?.data || []}
            loading={isLoading}
            fetching={isFetching}
            sortableFields={sortableFields}
            onSort={setSortSelected}
            sortSelected={sortSelected}
            searchPlaceholder="Search companies..."
            onSearch={setQueryValue}
            searchValue={queryValue}
            pagination={pagination}
            onPageChange={handlePageChange}
            showCheckbox={true}
            selectedItems={selectedCompanies}
            onItemSelect={handleCompanySelect}
            onSelectAll={handleSelectAll}
            onOpenEditColumns={() => dispatch(openColumnsModal("company"))}
            onRowClick={(row) => {
              dispatch(openContactInfoForCompany(row as CompanyAttributes))
            }}
          />
        </div>
      </div>
      <ExportLeads
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        selectedCount={selectedCompanies.length}
        totalAvailable={pagination.total}
      />
      {/* <Dialog open={!!infoCompanyContact} onOpenChange={(open) => !open && setInfoCompanyContact(null)}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed right-0 top-0 z-50 h-full w-[424px] border-l border-stone-200 bg-white shadow-xl focus:outline-none">
            <ContactInformation
              contact={infoCompanyContact}
              onClose={() => setInfoCompanyContact(null)}
              hideContactFields={true}
              hideCompanyActions={true}
            />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog> */}
      <Dialog open={!!infoCompanyContact} onOpenChange={(open) => !open && (setInfoCompanyContact(null), setInfoCompanyDetails(null))}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed right-0 top-0 z-50 h-full w-[424px] border-l border-stone-200 bg-white shadow-xl focus:outline-none">
            <ContactInformation
              contact={infoCompanyContact}
              company={infoCompanyDetails}
              onClose={() => {
                setInfoCompanyContact(null)
                setInfoCompanyDetails(null)
              }}
              hideContactFields={true}
              hideCompanyActions={true}
            />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <EditColumn />
    </CardContent>
    // </Card>
  )
}

export default CompaniesTable
